import '../style/global.css';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Badge, Dialog, DialogContent, DialogTitle, IconButton, List, ListItem, Pagination, Skeleton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import Box from "@mui/material/Box";
import React from "react";
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ScoreGrade, scoreColor, sentenceScoreAllSilence } from '../Score';
import { ActivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { AudioApi } from '../api/AudioApi';
import { ISentenceScore, ScoreApi } from '../api/ScoreApi';
import { Sentence, SentenceApi } from '../api/SentenceApi';
import background from '../asset/background-login.png';
import AddSelfStudyBlueIcon from '../asset/icon-add-self-study-blue.png';
import SpeakerBlueIcon from '../asset/icon-speaker-blue.svg';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useKaiTiFontStyles } from '../font/KaiTiFont';
import AddSentenceDialog from '../component/AddSentenceDialog';
import { pageWhiteCardSx } from '../style/pageWhiteCardSx';

export default function LeaderboardSentenceDetail() {

    useUserActivityTracker(ActivityEnum.Leaderboard)

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    // Check if screen is medium or smaller
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const { sentenceId } = useParams() as { sentenceId: number };

    const [isLoading, setIsLoading] = React.useState(true);
    const [showSkeleton, setShowSkeleton] = React.useState(false);

    const [sentence, setSentence] = React.useState<Sentence | null>(null);
    const [scoreList, setScoreList] = React.useState<ISentenceScore[]>([]);

    const [openDialog, setOpenDialog] = React.useState(false);
    const [selectedScore, setSelectedScore] = React.useState<ISentenceScore | null>(null);

    const [currentPage, setCurrentPage] = React.useState(1);
    const itemsPerPage = 5;

    const kaiTiFontClass = useKaiTiFontStyles();

    React.useEffect(() => {

        setTimeout(() => {
            setShowSkeleton(true);
        }, 300); // 300ms delay before showing shimmer

        Promise.all([
            SentenceApi.getById(sentenceId),
            ScoreApi.getScoreBySentence(sentenceId)
        ])
            .then(([sentenceResponse, scoreResponse]) => {
                if (sentenceResponse.status < 300) {
                    setSentence(sentenceResponse.data)
                } else {
                    throw new Error("Unable to SentenceApi.getById() " + sentenceResponse.status)
                }
                if (scoreResponse.status < 300) {
                    setScoreList(scoreResponse.data)
                } else {
                    throw new Error("Unable to ScoreApi.getScoreBySentence() " + scoreResponse.status)
                }
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [history, location, sentenceId, setErrorMessage]);

    const paginatedList = scoreList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const pageCount = Math.ceil(scoreList.length / itemsPerPage);


    const [addSentenceDialogOpen, setAddSentenceDialogOpen] = React.useState(false);

    const handleAddSentenceClick = () => {
        setAddSentenceDialogOpen(true);
    };

    const getAudio = (sentenceScoreId: number) => {
        AudioApi.getSentenceScoreAudio(sentenceScoreId)
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to ScoreApi.getScoreBySentence() " + response.status)
            })
            .then(audioData => playAudio(audioData))
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    };

    const getScoreStack = (sentenceScore: ISentenceScore) => {
        if (sentenceScoreAllSilence(sentenceScore)) {
            return <div>Sentence Incomplete</div>
        }
        return <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={1}>
            {sentenceScore.pinyin_list_spoken.map((pinyinElement, index) =>
                <Stack alignItems="center" justifyContent="center" key={index}
                    sx={{
                        backgroundColor: `${scoreColor(sentenceScore!.score_grade[index])}22`,   // 22 is 13.33% opacity
                        borderRadius: 2, px: 1
                    }}>
                    <Typography sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                        {pinyinElement}
                    </Typography>
                    <Typography className={kaiTiFontClass.customFont} sx={{ fontSize: '24px' }}>
                        {sentenceScore!.text[index]}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 300 }}>
                        {Math.round(sentenceScore.score[index])}
                    </Typography>
                </Stack>
            )}
        </Stack>

    }

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedScore(null);
    };

    return (
        <div style={{
            display: 'flex', // Use flex to allow easy centering
            flexDirection: 'column', // Changed to stack children vertically
            justifyContent: 'flex-start', // Center horizontally
            alignItems: 'center', // Center vertically
            minHeight: 'calc(100vh - 85px)', // Ensure the div takes at least the full viewport height
            width: '100%', // Ensure the div takes the full viewport width
            padding: 0, // Reset any padding
            backgroundImage: `url(${background})`, // Your background image
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover', // Cover the entire size of the div
            backgroundPosition: 'center', // Center the background image
        }}>
            <Box
                sx={{
                    mt: 4,
                    pt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '90%',
                    overflowY: 'auto',
                    position: 'relative',
                    p: 4,
                    ...pageWhiteCardSx,
                }}
            >
                {/* Title & Sort Row */}
                <Box sx={{ position: 'relative', width: '100%', textAlign: 'center', mb: 2 }}>
                    {/* Back arrow positioned absolutely */}
                    <IconButton
                        onClick={() => history.push('/leaderboard', { tab: 1 })}
                        sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#002A6E',
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    
                    {/* Centered title */}
                    <Typography variant="h2" sx={{ color: '#002A6E' }}>
                        Top Pronunciations
                    </Typography>
                </Box>

                {isLoading && showSkeleton ? (
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mt: 1 }}>
                        <Skeleton variant="rectangular" height={24} width={256} sx={{ borderRadius: 1 }} />
                    </Stack>
                ) : (
                <Box
                    component="span" // make the container inline like text
                    sx={{
                        display: 'inline', // inline container
                        maxWidth: '100%', 
                        textAlign: 'center',
                        px: 1,
                    }}
                >
                    <Typography
                        variant="h4"
                        component="span"  // inline text
                        sx={{ color: '#002A6E' }}
                    >
                        {sentence && sentence.text}
                    </Typography>

                    <Tooltip title="Add to Self-Study" arrow>
                        <IconButton
                        aria-label="Add to Self-Study"
                        onClick={handleAddSentenceClick}
                        sx={{
                            p: 0,
                            display: 'inline-flex', // keep icon inline
                            verticalAlign: 'text-bottom', // align with last line of text
                            ml: 1, 
                        }}
                        >
                        <img src={AddSelfStudyBlueIcon} style={{ width: '24px', height: '24px' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                )}

                {/* SentenceScore List */}
                {isLoading && showSkeleton ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <List sx={{ minHeight: '300px', transition: 'opacity 1s ease-in' }}>
                            {Array.from({ length: itemsPerPage }).map((_, i) => (
                                <Box key={i} sx={{ mb: 2, mt: 2 }}>
                                    <Skeleton variant="rectangular" height={24} width={384} sx={{ borderRadius: 1, mt: 6 }} />
                                </Box>
                            ))}
                        </List>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <List sx={{ width: 'fit-content' }}>
                            {paginatedList.map((sentenceScore, index) => (
                                <ListItem key={sentenceScore.id}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        {/* Rank */}
                                        <Box sx={{ width: 40, textAlign: 'center' }}>
                                            <Typography sx={{ fontWeight: 'bold' }}>
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </Typography>
                                        </Box>
                                        {/* Name */}
                                        <Box sx={{ width: { xs: 50, sm: 100 } }}>
                                            <Typography variant="h6">{sentenceScore.first_name}</Typography>
                                        </Box>
                                        {/* Speaker Icon */}
                                        <Box>
                                            <IconButton onClick={() => getAudio(sentenceScore.id)}>
                                                <img src={SpeakerBlueIcon} style={{ width: '36px', height: '36px' }} />
                                            </IconButton>
                                        </Box>
                                        {/* Total Score Badge */}
                                        {sentenceScoreAllSilence(sentenceScore) ? (
                                            <Box sx={{ width: 40, height: 32, borderRadius: '50%', mr: 1 }} />
                                        ) : (
                                            <Badge
                                                badgeContent={Math.round(sentenceScore.score_mean)}
                                                color="secondary"
                                                sx={{
                                                    '.MuiBadge-badge': {
                                                        backgroundColor: `${scoreColor(sentenceScore ? sentenceScore.passing_grade : ScoreGrade.Good)}22`,
                                                        fontSize: '1rem',
                                                        fontWeight: 'bold',
                                                        height: 48,
                                                        minWidth: 48,
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        top: '50%',
                                                        right: '50%',
                                                        transform: 'translate(50%, -50%)',
                                                    },
                                                }}
                                            >
                                                <Box sx={{ width: 32, height: 32, borderRadius: '50%', mr: 1 }} />
                                            </Badge>
                                        )}

                                        {isMobile ? (<>
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    onClick={() => {
                                                        setSelectedScore(sentenceScore); // store the clicked score
                                                        setOpenDialog(true);
                                                    }}
                                                    sx={{ textDecoration: 'underline', color: 'primary.main', cursor: 'pointer' }}
                                                >
                                                    Details
                                                </Typography>
                                            </Box>

                                            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                                                <DialogTitle>
                                                    Pronunciation
                                                    <IconButton
                                                        aria-label="close"
                                                        onClick={handleCloseDialog}
                                                        sx={{
                                                            position: 'absolute',
                                                            right: 8,
                                                            top: 8,
                                                        }}
                                                    >
                                                        <CloseRoundedIcon />
                                                    </IconButton>
                                                </DialogTitle>
                                                <DialogContent dividers>
                                                      {selectedScore && getScoreStack(selectedScore)}
                                                </DialogContent>
                                            </Dialog>
                                        </>) : (
                                            <Box sx={{ flexGrow: 1, ml: 2 }}>
                                                {getScoreStack(sentenceScore)}
                                            </Box>
                                        )}
                                    </Stack>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}


                {/* Pagination */}
                {/* Pagination must not depend on `showSkeleton`: fast API responses can finish
                    before the 300ms skeleton delay, leaving showSkeleton false and hiding pages. */}
                {!isLoading && pageCount > 1 && (
                    <Pagination
                        count={pageCount}
                        page={currentPage}
                        onChange={(_, value) => setCurrentPage(value)}
                        color="primary"
                        sx={{ mt: 2 }}
                    />
                )}
            </Box>
            
            {/* Add Sentence Confirmation Dialog */}
            <AddSentenceDialog
                open={addSentenceDialogOpen}
                sentence={sentence}
                onClose={() => setAddSentenceDialogOpen(false)}
            />
        </div >
    );
}
